import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import {
  Android as AiIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Chat as ChatIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Extension as IntegrationIcon,
  Group as GroupIcon,
  Http as ApiIcon,
  Notifications as CampaignIcon,
  Phone as PhoneIcon,
  Queue as QueueIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  Star as StarIcon,
  ViewList as KanbanIcon,
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import { useStyles } from "./planManagerStyles";

export function PlanFormImproved(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading, variant = "page" } = props;
  const classes = useStyles();

  const [record, setRecord] = useState({
    name: '',
    users: 0,
    connections: 0,
    queues: 0,
    value: 0,
    useCampaigns: true,
    useSchedules: true,
    useInternalChat: true,
    useExternalApi: true,
    useKanban: true,
    useOpenAi: true,
    useIntegrations: true,
    useInternal: true
  });

  useEffect(() => {
    setRecord(initialValue);
  }, [initialValue]);

  const handleSubmit = async (data) => {
    onSubmit(data);
  };

  return (
    <Card className={variant === "dialog" ? classes.formCardDialog : classes.formCard}>
      <CardContent className={classes.formCardContent}>
        <Typography variant="h5" className={classes.formTitle}>
          <SettingsIcon />
          {record.id ? "Editar Plan" : "Crear Nuevo Plan"}
        </Typography>

        <Formik
          enableReinitialize
          initialValues={record}
          onSubmit={(values, { resetForm }) =>
            setTimeout(() => {
              handleSubmit(values);
              resetForm();
            }, 500)
          }
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid container spacing={1} className={classes.formGrid}>
                {/* Información Básica */}
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <StarIcon />
                    Información Básica del Plan
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("plans.form.name")}
                      name="name"
                      value={values.name || ""}
                      onChange={(e) => setFieldValue('name', e.target.value)}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <StarIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("plans.form.users")}
                      name="users"
                      value={values.users || ""}
                      onChange={(e) => setFieldValue('users', e.target.value)}
                      variant="outlined"
                      fullWidth
                      type="number"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <GroupIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("plans.form.connections")}
                      name="connections"
                      value={values.connections || ""}
                      onChange={(e) => setFieldValue('connections', e.target.value)}
                      variant="outlined"
                      fullWidth
                      type="number"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <PhoneIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("plans.form.queues")}
                      name="queues"
                      value={values.queues || ""}
                      onChange={(e) => setFieldValue('queues', e.target.value)}
                      variant="outlined"
                      fullWidth
                      type="number"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <QueueIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("plans.form.value")}
                      name="value"
                      value={values.value || ""}
                      onChange={(e) => setFieldValue('value', e.target.value)}
                      variant="outlined"
                      fullWidth
                      type="text"
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <MoneyIcon style={{ marginRight: 8, color: "#666" }} />
                      }}
                    />
                  </div>
                </Grid>

                {/* Funcionalidades */}
                <Grid item xs={12}>
                  <Divider style={{ margin: "15px 0" }} />
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <SettingsIcon />
                    Funcionalidades del Plan
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.campaigns")}</InputLabel>
                      <Field
                        as={Select}
                        name="useCampaigns"
                        label={i18n.t("plans.form.campaigns")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CampaignIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CampaignIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.schedules")}</InputLabel>
                      <Field
                        as={Select}
                        name="useSchedules"
                        label={i18n.t("plans.form.schedules")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ScheduleIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ScheduleIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.internalChat")}</InputLabel>
                      <Field
                        as={Select}
                        name="useInternalChat"
                        label={i18n.t("plans.form.internalChat")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ChatIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ChatIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.externalApi")}</InputLabel>
                      <Field
                        as={Select}
                        name="useExternalApi"
                        label={i18n.t("plans.form.externalApi")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ApiIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ApiIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.kanban")}</InputLabel>
                      <Field
                        as={Select}
                        name="useKanban"
                        label={i18n.t("plans.form.kanban")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <KanbanIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <KanbanIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.openAi")}</InputLabel>
                      <Field
                        as={Select}
                        name="useOpenAi"
                        label={i18n.t("plans.form.openAi")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AiIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AiIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.integrations")}</InputLabel>
                      <Field
                        as={Select}
                        name="useIntegrations"
                        label={i18n.t("plans.form.integrations")}
                      >
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <IntegrationIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.enabled")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <IntegrationIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.disabled")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <div className={classes.fieldContainer}>
                    <FormControl variant="outlined" fullWidth className={classes.selectField}>
                      <InputLabel>{i18n.t("plans.form.internalUse")}</InputLabel>
                      <Field
                        as={Select}
                        name="useInternal"
                        label={i18n.t("plans.form.internalUse")}
                      >
                        <MenuItem value={false}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <BusinessIcon style={{ fontSize: 16 }} />
                            {i18n.t("plans.form.yes")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={true}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <BusinessIcon style={{ fontSize: 16, color: "#666" }} />
                            {i18n.t("plans.form.no")}
                          </Box>
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </Grid>
              </Grid>

              {/* Botones de Acción */}
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  className={`${classes.actionButton} ${classes.secondaryButton}`}
                  onClick={onCancel}
                  startIcon={<ClearIcon />}
                  disabled={loading}
                >
                  {i18n.t("plans.form.clear")}
                </Button>

                {record.id !== undefined && (
                  <Button
                    variant="contained"
                    className={`${classes.actionButton} ${classes.errorButton}`}
                    onClick={() => onDelete(record)}
                    startIcon={<DeleteIcon />}
                    disabled={loading}
                  >
                    {i18n.t("plans.form.delete")}
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  className={`${classes.actionButton} ${classes.primaryButton}`}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {loading ? "Guardando..." : i18n.t("plans.form.save")}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}

export default PlanFormImproved;
